import { HttpEvent, HttpEventType } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { Observable, Observer, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AlertService, AlertType } from '../../services/alert/alert.service';
import { S3FileManagerService } from '../../services/s3-file-manager/s3-file-manager.service';
import { ModalComponent, ModalConfig } from '../modals/modal/modal.component';

interface StagedUploadFile {
  file: File;
  subPath: string;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  errorMsg: string;
}

interface StagedGroup {
  folderName: string | null;
  items: { staged: StagedUploadFile; originalIndex: number }[];
}

@Component({
  selector: 'app-s3-file-upload-modal',
  standalone: false,
  templateUrl: './s3-file-upload-modal.component.html',
  styleUrl: './s3-file-upload-modal.component.scss',
})
export class S3FileUploadModalComponent {
  @Input() currentPath = '';

  @Output() refreshList = new EventEmitter<void>();

  @ViewChild('modal') modal: ModalComponent;

  static readonly ALLOWED_EXTENSIONS = new Set([
    'pdf',
    'csv',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'html',
    'txt',
    'md',
    'gif',
    'jpeg',
    'jpg',
    'png',
    'mp4',
  ]);

  static readonly MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024 * 1024; // 1 GB

  readonly modalConfig: ModalConfig = {
    titleText: 'Upload Files',
    headerClass: ['modal-header', 'bg-primary', 'text-white'],
    customFooter: true,
    hideCloseButton: true,
  };

  readonly getFileIcon = S3FileUploadModalComponent.getFileIcon;

  readonly getStagedDisplayName =
    S3FileUploadModalComponent.getStagedDisplayName;

  readonly groupStatus = S3FileUploadModalComponent.groupStatus;

  readonly groupProgress = S3FileUploadModalComponent.groupProgress;

  readonly getFileRelativeName = S3FileUploadModalComponent.getFileRelativeName;

  stagedFiles: StagedUploadFile[] = [];

  uploadSaving = false;

  uploadDropzoneActive = false;

  uploadErrorMessage = '';

  constructor(
    private alertService: AlertService,
    private s3FileManagerService: S3FileManagerService
  ) {}

  open(): void {
    this.modal.open();
  }

  openWithEntries(entries: { file: File; subPath: string }[]): void {
    this.reset();
    this.stageFileEntries(entries);
    this.modal.open();
  }

  onUploadFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.stageFileEntries(
        Array.from(input.files).map((file) => ({ file, subPath: '' }))
      );
      input.value = '';
    }
  }

  onUploadFolderChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.stageFileEntries(
        Array.from(input.files).map((file) => ({
          file,
          subPath: file.webkitRelativePath
            ? `${file.webkitRelativePath.split('/').slice(0, -1).join('/')}/`
            : '',
        }))
      );
      input.value = '';
    }
  }

  async handleUploadFileDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.uploadDropzoneActive = false;
    if (!event.dataTransfer?.items.length) return;
    // Collect entries synchronously before any awaits (items become inaccessible after async gap)
    const entries = Array.from(event.dataTransfer.items)
      .map((item) => item.webkitGetAsEntry())
      .filter((entry): entry is FileSystemEntry => entry !== null);
    const allFileEntries = (
      await Promise.all(entries.map((entry) => this.readFolderEntry(entry, '')))
    ).flat();
    this.stageFileEntries(allFileEntries);
  }

  stageFiles(files: File[]): void {
    this.stageFileEntries(files.map((file) => ({ file, subPath: '' })));
  }

  stageFileEntries(entries: { file: File; subPath: string }[]): void {
    const rejected: string[] = [];
    const valid: { file: File; subPath: string }[] = [];
    entries.forEach(({ file, subPath }) => {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!S3FileUploadModalComponent.ALLOWED_EXTENSIONS.has(ext)) {
        rejected.push(`"${file.name}" is not a supported file type`);
      } else if (file.size > S3FileUploadModalComponent.MAX_FILE_SIZE_BYTES) {
        rejected.push(`"${file.name}" exceeds the 1 GB size limit`);
      } else {
        valid.push({ file, subPath });
      }
    });
    if (rejected.length) {
      this.uploadErrorMessage = rejected.join('; ');
    }
    const newEntries: StagedUploadFile[] = valid.map(({ file, subPath }) => ({
      file,
      subPath,
      progress: 0,
      status: 'pending',
      errorMsg: '',
    }));
    this.stagedFiles = [...this.stagedFiles, ...newEntries];
    this.refreshGroupedFiles();
  }

  private static readAllDirEntries(
    reader: FileSystemDirectoryReader
  ): Promise<FileSystemEntry[]> {
    return new Promise((resolve) => {
      const accumulated: FileSystemEntry[] = [];
      const read = () => {
        reader.readEntries((batch) => {
          if (!batch.length) {
            resolve(accumulated);
          } else {
            batch.forEach((e) => accumulated.push(e));
            read();
          }
        });
      };
      read();
    });
  }

  private async readFolderEntry(
    entry: FileSystemEntry,
    basePath: string
  ): Promise<{ file: File; subPath: string }[]> {
    if (entry.isFile) {
      return new Promise<{ file: File; subPath: string }[]>((resolve) => {
        (entry as FileSystemFileEntry).file((file) => {
          resolve([{ file, subPath: basePath }]);
        });
      });
    }
    const dirEntry = entry as FileSystemDirectoryEntry;
    const children = await S3FileUploadModalComponent.readAllDirEntries(
      dirEntry.createReader()
    );
    const nested = await Promise.all(
      children.map((child) =>
        this.readFolderEntry(child, `${basePath}${entry.name}/`)
      )
    );
    return nested.flat();
  }

  removeStagedFile(index: number): void {
    this.stagedFiles = this.stagedFiles.filter((_, i) => i !== index);
    this.refreshGroupedFiles();
  }

  private handleUploadProgress(
    event: HttpEvent<unknown> | null,
    index: number,
    observer: Observer<null>
  ): void {
    if (!event) {
      observer.next(null);
      observer.complete();
      return;
    }
    if (event.type === HttpEventType.UploadProgress && event.total) {
      this.stagedFiles[index].progress = Math.round(
        (100 * event.loaded) / event.total
      );
    } else if (event.type === HttpEventType.Response) {
      this.stagedFiles[index].status = 'done';
      this.stagedFiles[index].progress = 100;
      observer.next(null);
      observer.complete();
    }
  }

  private createUploadStream(
    staged: StagedUploadFile,
    index: number
  ): Observable<null> {
    staged.status = 'uploading';
    const directory = staged.subPath
      ? this.currentPath + staged.subPath
      : this.currentPath || undefined;
    return new Observable<null>((observer) => {
      this.s3FileManagerService
        .uploadFileWithProgress(staged.file, directory)
        .pipe(
          catchError(() => {
            this.stagedFiles[index].status = 'error';
            this.stagedFiles[index].errorMsg = 'Upload failed';
            return of(null);
          })
        )
        .subscribe({
          next: (event) => this.handleUploadProgress(event, index, observer),
          error: () => {
            observer.next(null);
            observer.complete();
          },
        });
    });
  }

  private onAllUploadsComplete(): void {
    this.uploadSaving = false;
    const anyErrors = this.stagedFiles.some((f) => f.status === 'error');
    if (!anyErrors) {
      this.modal.close();
      this.refreshList.emit();
      this.alertService.showAlert(
        'Files uploaded successfully',
        undefined,
        AlertType.PRIMARY
      );
    } else {
      this.refreshList.emit();
    }
  }

  saveUploadFiles(): void {
    if (!this.stagedFiles.length) return;
    this.uploadSaving = true;
    const streams = this.stagedFiles.map((staged, index) =>
      this.createUploadStream(staged, index)
    );
    forkJoin(streams).subscribe(() => this.onAllUploadsComplete());
  }

  reset(): void {
    this.stagedFiles = [];
    this.groupedStagedFiles = [];
    this.uploadSaving = false;
    this.uploadDropzoneActive = false;
    this.uploadErrorMessage = '';
  }

  groupedStagedFiles: StagedGroup[] = [];

  private refreshGroupedFiles(): void {
    const groupMap = new Map<
      string | null,
      { staged: StagedUploadFile; originalIndex: number }[]
    >();
    this.stagedFiles.forEach((staged, originalIndex) => {
      const root = staged.subPath ? staged.subPath.split('/')[0] : null;
      if (!groupMap.has(root)) {
        groupMap.set(root, []);
      }
      const items = groupMap.get(root);
      if (items) {
        items.push({ staged, originalIndex });
      }
    });
    this.groupedStagedFiles = Array.from(groupMap.entries()).map(
      ([folderName, items]) => ({ folderName, items })
    );
  }

  static groupStatus(
    group: StagedGroup
  ): 'pending' | 'uploading' | 'done' | 'error' {
    const statuses = group.items.map((i) => i.staged.status);
    if (statuses.some((s) => s === 'error')) return 'error';
    if (statuses.every((s) => s === 'done')) return 'done';
    if (statuses.some((s) => s === 'uploading')) return 'uploading';
    return 'pending';
  }

  static groupProgress(group: StagedGroup): number {
    const progs = group.items.map((i) => i.staged.progress);
    return Math.round(progs.reduce((a, b) => a + b, 0) / progs.length);
  }

  static getFileRelativeName(
    staged: StagedUploadFile,
    folderName: string
  ): string {
    const withoutRoot = staged.subPath.startsWith(`${folderName}/`)
      ? staged.subPath.slice(folderName.length + 1)
      : staged.subPath;
    return `${withoutRoot}${staged.file.name}`;
  }

  static getStagedDisplayName(staged: StagedUploadFile): string {
    return staged.subPath
      ? `${staged.subPath}${staged.file.name}`
      : staged.file.name;
  }

  static getFileIcon(key: string): string {
    if (key.endsWith('/')) return 'bi-folder-fill';
    const ext = key.split('.').pop()?.toLowerCase() ?? '';
    const iconMap: Record<string, string> = {
      png: 'bi-file-image',
      jpg: 'bi-file-image',
      jpeg: 'bi-file-image',
      gif: 'bi-file-image',
      svg: 'bi-file-image',
      mp4: 'bi-file-play',
      mov: 'bi-file-play',
      avi: 'bi-file-play',
      mkv: 'bi-file-play',
      mp3: 'bi-file-music',
      wav: 'bi-file-music',
      pdf: 'bi-file-pdf',
      doc: 'bi-file-word',
      docx: 'bi-file-word',
      xls: 'bi-file-earmark-spreadsheet',
      xlsx: 'bi-file-earmark-spreadsheet',
      csv: 'bi-file-earmark-spreadsheet',
      zip: 'bi-file-zip',
      gz: 'bi-file-zip',
      txt: 'bi-file-text',
      md: 'bi-file-text',
      json: 'bi-file-code',
      xml: 'bi-file-code',
      js: 'bi-file-code',
      ts: 'bi-file-code',
    };
    return iconMap[ext] ?? 'bi-file';
  }
}
